import { flip, FloatingPortal, offset, useFloating, useHover, useInteractions } from '@floating-ui/react'
import ctl from '@netlify/classnames-template-literals'
import { ReactNode, useState } from 'react'

export type ViewToggleProps<T extends string> = {
	value: T
	onChange: (value: T) => void
	options: Array<{
		value: T
		label: string
		icon: ReactNode
	}>
	disabled?: boolean
}

export function ViewToggle<T extends string>({ value, onChange, options, disabled }: ViewToggleProps<T>) {
	return (
		<div className={ctl(`${disabled ? 'opacity-50 cursor-not-allowed' : ''}`)}>
			{options.map((option) => (
				<ViewToggleOption
					key={option.value}
					value={value}
					onChange={disabled ? () => {} : onChange}
					option={option}
					options={options}
					disabled={disabled}
				/>
			))}
		</div>
	)
}

function ViewToggleOption<T extends string>(
	{ value, onChange, option, disabled }: ViewToggleProps<T> & { option: ViewToggleProps<T>['options'][number] },
) {
	const [isOpen, setIsOpen] = useState(false)

	const open = disabled ? false : isOpen

	const { refs, floatingStyles, context } = useFloating({
		open,
		onOpenChange: setIsOpen,
		middleware: [flip(), offset(5)],
	})
	const hover = useHover(context)
	const { getReferenceProps, getFloatingProps } = useInteractions([hover])

	return (
		<>
			<button
				key={option.value}
				onClick={() => onChange(option.value)}
				ref={refs.setReference}
				{...getReferenceProps()}
				className={ctl(`
					${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
					${
					value === option.value
						? 'bg-purple-100 text-purple-500 ring-purple-300 z-10'
						: 'bg-white text-grey-500 ring-grey-200'
				}
					relative
					p-2 ring
					first:rounded-l-md last:rounded-r-md
                    `)}
			>
				{option.icon}
			</button>
			{open && (
				<FloatingPortal>
					<div
						className="[contain:layout] z-100 text-xs px-2 py-1 bg-white rounded shadow-sm"
						ref={refs.setFloating}
						style={floatingStyles}
						{...getFloatingProps()}
					>
						{option.label}
					</div>
				</FloatingPortal>
			)}
		</>
	)
}
